import Button from 'ext-ui/components/Button/Button'
import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import PayBillFlyout from 'src/component-flyout/PayBillFlyout/PayBillFlyout'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import Box from 'src/components/Box/Box'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUserExplicitOrganizationIdFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectLoadedFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { canManageBillPay } from 'src/utils/billPayAccess'
import { isBillPayM3PaymentsEnabled } from 'src/utils/featureFlags'

import style from './PaymentsPage.style'

/**
 * Bill Pay M3 — the Payments page (EX-40607).
 *
 * This slice is the SHELL only: route, nav entry, and the three gates (flag,
 * SaaS tier, role). The pay-a-vendor flyout (EX-40561/40563), the payments
 * table, and the "Pay Bill" CTA land on top of this — deliberately not stubbed
 * here, because a CTA that opens nothing is worse than no CTA (a real review
 * finding on the M1 page, #7311).
 *
 * Gating is character-for-character the Vendors page's, via the same shared
 * helpers, so the three Bill Pay surfaces (Bank Account, Vendors, Payments)
 * can never disagree about who gets in.
 */
const PaymentsPage: React.FC = () => {
  const translate = useTranslate()
  const currentUser = useCurrentUser()
  const appName = useSelector(selectAppName)
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  const loadedRemoteConfig = useSelector(selectLoadedFromRemoteConfigState)
  const organizationId = useSelector(selectUserExplicitOrganizationIdFromState)

  // The page owns the Pay Bill flyout's open/close state; the flyout is a
  // controlled child (same ownership split as VendorsPage ↔ AddVendorFlyout).
  const [isPayBillOpen, setIsPayBillOpen] = useState(false)

  const billPayPaymentsEnabled = isBillPayM3PaymentsEnabled(
    remoteConfig,
    organizationId,
  )

  // Gated on the flag so a user without M3 never fires a dark features request
  // (same convention as VendorsPage / BankAccountsPage, EX-40413 review fix).
  const { data: userFeatures, isLoading: userFeaturesLoading } =
    useQueryUserFeatures(
      [UserFeature.VIEW_PAYABLES, UserFeature.ORGANIZATION_USER_STATISTICS],
      {
        enabled: billPayPaymentsEnabled,
      },
    )

  const canViewPayables = userFeatures?.[UserFeature.VIEW_PAYABLES] ?? false
  // Owner/admin + full-visibility bookkeepers (EX-40473). Shared helper so this
  // gate and the nav item can never diverge — see src/utils/billPayAccess.
  const hasAccess =
    billPayPaymentsEnabled &&
    canManageBillPay({
      hasOrgAdminPermissions: currentUser?.hasOrgAdminPermissions,
      organizationRole: currentUser?.organizationRole,
      orgUserStatisticsEnabled:
        userFeatures?.[UserFeature.ORGANIZATION_USER_STATISTICS],
    }) &&
    canViewPayables

  if (
    !loadedRemoteConfig ||
    currentUser === undefined ||
    (billPayPaymentsEnabled && userFeaturesLoading)
  ) {
    return <LoadingBackgroundPage />
  }

  if (!hasAccess) {
    return <NotFoundBackgroundPage />
  }

  return (
    <LayoutPage fixedHeight>
      <Helmet>
        <title>{`${translate('Payments')} | ${appName}`}</title>
      </Helmet>
      <div className={'PaymentsPage'} css={style} data-testid={'payments-page'}>
        <div className={'payments-header'}>
          <h1 className={'payments-title'}>{translate('Payments')}</h1>
          {/* Primary CTA in the header, matching the Vendors page. The flyout
              inherits the page's flag + tier + role gating by being mounted
              inside it — no new gate. */}
          <Button
            variant={'primary'}
            onClick={() => setIsPayBillOpen(true)}
            data-testid={'pay-bill-button'}
          >
            {translate('Pay Bill')}
          </Button>
        </div>
        {/* Same bordered container the Vendors page wraps both its empty state
            and its populated table in — the table drops in here unchanged. */}
        <Box noPadding noShadow outlineBorder className={'payments-box'}>
          <div
            className={'payments-empty'}
            data-testid={'payments-empty-state'}
          >
            <h2 className={'payments-empty-title'}>
              {translate('No payments yet')}
            </h2>
            <p className={'payments-empty-description'}>
              {translate(
                'Payments you send to your vendors will appear here once you pay your first bill.',
              )}
            </p>
          </div>
        </Box>
        {/* organizationId is always resolved once `hasAccess` is true (the M3
            flag is derived from it), so this renders in practice; the guard
            just satisfies the flyout's `string` org-id contract. */}
        {organizationId && (
          <PayBillFlyout
            isOpen={isPayBillOpen}
            onClose={() => setIsPayBillOpen(false)}
            organizationId={organizationId}
          />
        )}
      </div>
    </LayoutPage>
  )
}

export default PaymentsPage
